import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft, FileDown, Printer } from 'lucide-react';
import { db } from '@/lib/firebase';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { formatCurrency, formatDate } from '@/lib/utils';
import { generateInvoicePdfClient } from '@/lib/generateInvoicePdfClient';
import type { Order } from '@/types/schemas';

export function OrderReceiptPage() {
  const { orderId = '' } = useParams();

  const { data: order } = useQuery({
    queryKey: ['order', orderId],
    queryFn: async (): Promise<Order | null> => {
      const snap = await getDoc(doc(db, 'orders', orderId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Omit<Order, 'id'>) };
    },
    enabled: !!orderId,
    refetchInterval: 3000,
  });

  function handleDownloadPdf() {
    if (!order) return;
    generateInvoicePdfClient({
      businessName: 'CHHABIGHAR',
      tagline: 'Powered by NexaRise',
      invoiceNumber: order.invoiceNumber ?? order.id.slice(0, 8).toUpperCase(),
      date: (() => {
        const ts = order.createdAt as unknown;
        if (!ts) return new Date();
        if (typeof (ts as { toMillis?: () => number }).toMillis === 'function') {
          return new Date((ts as { toMillis: () => number }).toMillis());
        }
        return new Date(ts as number);
      })(),
      customerName: order.customerSnapshot?.name ?? 'Customer',
      customerPhone: order.customerSnapshot?.phone ?? '',
      items: order.items.map((it) => ({
        itemName: it.itemName,
        sku: it.sku,
        quantity: it.quantity,
        price: it.price,
        total: it.total,
      })),
      subtotal: order.subtotal,
      tax: 0,
      discount: order.discount,
      total: order.totalAmount,
      paymentMethod: order.paymentMethod,
    });
  }

  if (!order) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div>
      <PageHeader
        title="Order receipt"
        subtitle={order.invoiceNumber ?? order.id.slice(0, 8)}
        actions={
          <>
            <Button asChild variant="ghost" size="sm">
              <Link to="/pos">
                <ArrowLeft className="h-4 w-4" /> Back to POS
              </Link>
            </Button>
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4" /> Print
            </Button>
            <Button variant="gold" onClick={handleDownloadPdf}>
              <FileDown className="h-4 w-4" />
              <span className="hidden sm:inline">Generate Invoice PDF</span>
            </Button>
          </>
        }
      />

      <Card className="mx-auto max-w-2xl">
        <CardContent className="p-4 sm:p-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <div className="font-serif text-xl sm:text-2xl">CHHABIGHAR</div>
              <div className="text-xs text-muted-foreground">Tax Invoice</div>
            </div>
            <img src="/chhabighar.png" alt="" className="h-10 w-10 rounded sm:h-12 sm:w-12" />
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Bill to</div>
              <div className="font-medium">{order.customerSnapshot?.name}</div>
              <div className="text-xs">{order.customerSnapshot?.phone}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Invoice</div>
              <div className="font-medium">
                {order.invoiceNumber ?? order.id.slice(0, 8).toUpperCase()}
              </div>
              <div className="text-xs">
                {formatDate(
                  // @ts-expect-error firestore timestamp
                  typeof order.createdAt?.toMillis === 'function'
                    ? // @ts-expect-error firestore timestamp
                      order.createdAt.toMillis()
                    : order.createdAt,
                )}
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th className="pb-2 text-left">Item</th>
                <th className="pb-2 text-right">Qty</th>
                <th className="hidden pb-2 text-right sm:table-cell">Price</th>
                <th className="pb-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it, i) => (
                <tr key={i} className="border-t">
                  <td className="py-2 pr-2">{it.itemName}</td>
                  <td className="py-2 text-right tabular-nums">{it.quantity}</td>
                  <td className="hidden py-2 text-right tabular-nums sm:table-cell">
                    {formatCurrency(it.price)}
                  </td>
                  <td className="py-2 text-right tabular-nums">{formatCurrency(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <Separator className="my-4" />

          <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
            <Row label="Subtotal" value={order.subtotal} />
            {order.deliveryCharge > 0 && <Row label="Delivery Charge" value={order.deliveryCharge} />}
            {order.discount > 0 && <Row label="Discount" value={-order.discount} />}
            <Separator />
            <div className="flex justify-between font-serif text-lg">
              <span>Total</span>
              <span className="tabular-nums">{formatCurrency(order.totalAmount)}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Payment</span>
              <span className="uppercase">{order.paymentMethod}</span>
            </div>
          </div>

          <div className="mt-8 text-center text-xs text-muted-foreground">
            Thank you for shopping at CHHABIGHAR · ছবি ঘর
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{formatCurrency(value)}</span>
    </div>
  );
}
